import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import {
  getGraduationById,
  batchUpdateAttendance,
  refundNonAttendeeOverride
} from '../../../../services/crmApi';
import LoadingSpinner from '../../../Common/LoadingSpinner';

const depositStatusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-800',
  PAID: 'bg-green-100 text-green-800',
  FORFEITED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-blue-100 text-blue-800',
  WAIVED: 'bg-purple-100 text-purple-800'
};

const GraduationAttendanceCheckin = () => {
  const { id } = useParams();
  const [graduation, setGraduation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchGraduation();
  }, [id]);

  const fetchGraduation = async () => {
    try {
      setLoading(true);
      const response = await getGraduationById(id);
      const data = response.data.data;
      setGraduation(data);
      const initial = {};
      (data.students || []).forEach((s) => {
        const studentId = s.studentId?._id || s.studentId;
        const enrollmentId = s.enrollmentId?._id || s.enrollmentId;
        initial[`${studentId}-${enrollmentId}`] = !!s.attendance?.confirmed;
      });
      setAttendance(initial);
    } catch (error) {
      console.error('Error fetching graduation:', error);
      toast.error('Failed to load graduation');
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = (studentId, enrollmentId) => {
    const key = `${studentId}-${enrollmentId}`;
    setAttendance({ ...attendance, [key]: !attendance[key] });
  };

  const handleSubmit = async () => {
    const attendances = Object.entries(attendance).map(([key, attended]) => {
      const [studentId, enrollmentId] = key.split('-');
      return { studentId, enrollmentId, attended };
    });
    try {
      setSubmitting(true);
      await batchUpdateAttendance(id, attendances);
      toast.success('Attendance updated');
      fetchGraduation();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefundOverride = async (studentId, enrollmentId) => {
    const reason = window.prompt('Override reason:');
    if (!reason) return;
    try {
      await refundNonAttendeeOverride(id, studentId, enrollmentId, reason);
      toast.success('Refund processed');
      fetchGraduation();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process refund');
    }
  };

  if (loading) return <LoadingSpinner />;

  const filteredStudents = (graduation.students || []).filter((s) => {
    const name = (s.studentInfo?.name || s.studentId?.fullName || '').toLowerCase();
    return name.includes(filter.toLowerCase());
  });

  const attendedCount = Object.values(attendance).filter(Boolean).length;
  const totalCount = graduation.students?.length || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Check-In</h1>
          <p className="text-gray-600">{graduation.name}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Attended</p>
          <p className="text-2xl font-bold text-indigo-600">{attendedCount} / {totalCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <input
          type="text"
          placeholder="Search students..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full sm:w-80 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Attended</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Enrollment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Deposit Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => {
                const studentId = student.studentId?._id || student.studentId;
                const enrollmentId = student.enrollmentId?._id || student.enrollmentId;
                const key = `${studentId}-${enrollmentId}`;
                const attended = attendance[key] || false;
                const canEnter = ['PAID', 'REFUNDED', 'WAIVED'].includes(student.deposit?.status);
                return (
                  <tr key={key} className={attended ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={attended}
                        disabled={!canEnter}
                        onChange={() => toggleAttendance(studentId, enrollmentId)}
                        className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{student.studentInfo?.name || student.studentId?.fullName || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{student.studentId?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{student.enrollmentId?.enrollmentNumber || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${depositStatusColors[student.deposit?.status] || 'bg-gray-100'}`}>
                        {student.deposit?.status || 'PENDING'}
                      </span>
                      {!canEnter && <span className="ml-2 text-xs text-red-600">(Entry blocked)</span>}
                    </td>
                    <td className="px-4 py-3">
                      {!attended && student.deposit?.status === 'PAID' && (
                        <button
                          type="button"
                          title="Refund Override"
                          aria-label="Refund Override"
                          onClick={() => handleRefundOverride(studentId, enrollmentId)}
                          className="p-1.5 rounded transition-all text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          <BanknotesIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving...' : 'Save Attendance'}
        </button>
      </div>
    </div>
  );
};

export default GraduationAttendanceCheckin;
